import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, Copy, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BlockRenderer } from './BlockRenderer';
import { isLandingBlockVisible, useLandingMe } from './useLandingMe';
import type { LandingBlock, LandingBlockType } from '../../../../shared/types/people-landing';

/**
 * Grilla bento del People Landing (Fase 3). 4 columnas en md+, una en mobile.
 * `span` ausente = 4 = ancho completo: una página vieja renderiza igual que la
 * lista vertical de antes. En modo edición cada bloque es sortable (dnd-kit) y
 * lleva su overlay: handle, Editar, ancho (¼ ½ ¾ Full), Duplicar, ↑↓ (fallback
 * de teclado) y Borrar. Los cambios van al borrador del padre; se persisten con
 * "Guardar".
 */
const SPAN_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
};

export interface LandingBlocksGridProps {
  blocks: LandingBlock[];
  editing: boolean;
  imageUrls: Record<string, string>;
  blockTypeLabel: (type: LandingBlockType) => string;
  onReorder: (blocks: LandingBlock[]) => void;
  onEdit: (block: LandingBlock) => void;
  onSetSpan: (block: LandingBlock, span: 1 | 2 | 3 | 4) => void;
  onDuplicate: (block: LandingBlock) => void;
  onDelete: (block: LandingBlock) => void;
}

export function LandingBlocksGrid(props: LandingBlocksGridProps) {
  const { editing, imageUrls } = props;
  const reduceMotion = useReducedMotion();
  // Fuera de edición, los bloques dinámicos que no aplican al que mira se
  // quitan de la grilla (sin hueco). En edición People los ve todos.
  const { data: me, isLoading: meLoading } = useLandingMe();
  const blocks = editing || meLoading ? props.blocks : props.blocks.filter((b) => isLandingBlockVisible(b.type, me));
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const gridClass = 'grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-8 auto-rows-auto items-stretch';

  if (!editing) {
    return (
      <div className={gridClass}>
        {blocks.map((block, idx) => (
          <motion.div
            key={block.id}
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '0px 0px -40px 0px' }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: Math.min(idx, 6) * 0.06 }}
            className={cn('min-w-0', SPAN_CLASS[block.span ?? 4], block.rowSpan === 2 && 'md:row-span-2')}
          >
            <BlockRenderer block={block} imageUrls={imageUrls} />
          </motion.div>
        ))}
      </div>
    );
  }

  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = blocks.findIndex((b) => b.id === active.id);
    const to = blocks.findIndex((b) => b.id === over.id);
    if (from < 0 || to < 0) return;
    props.onReorder(arrayMove(blocks, from, to));
  };
  const active = activeId ? blocks.find((b) => b.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={blocks.map((b) => b.id)} strategy={rectSortingStrategy}>
        <div className={gridClass}>
          {blocks.map((block, idx) => (
            <SortableBlock
              key={block.id}
              block={block}
              idx={idx}
              total={blocks.length}
              {...props}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {active ? (
          <div className="rounded-xl border-2 border-dashed border-brand-green/70 bg-background/80 backdrop-blur-sm px-4 py-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground shadow-lg">
            {props.blockTypeLabel(active.type)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function SortableBlock({
  block,
  idx,
  total,
  blocks,
  imageUrls,
  blockTypeLabel,
  onReorder,
  onEdit,
  onSetSpan,
  onDuplicate,
  onDelete,
}: LandingBlocksGridProps & { block: LandingBlock; idx: number; total: number }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const span = block.span ?? 4;
  const move = (dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= total) return;
    onReorder(arrayMove(blocks, idx, j));
  };
  const widths: { v: 1 | 2 | 3 | 4; label: string }[] = [
    { v: 1, label: '¼' },
    { v: 2, label: '½' },
    { v: 3, label: '¾' },
    { v: 4, label: t('people_landing.editor.width_full', 'Full') },
  ];

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'relative group min-w-0 rounded-xl border-2 border-dashed border-transparent hover:border-border transition-colors',
        SPAN_CLASS[span],
        block.rowSpan === 2 && 'md:row-span-2',
        isDragging && 'opacity-40'
      )}
      {...attributes}
    >
      {/* Overlay de edición */}
      <div className="absolute -top-3 left-3 right-3 z-20 flex items-center justify-between gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...listeners}
          aria-label={t('people_landing.editor.drag', 'Arrastrar para mover')}
          className="flex h-7 items-center gap-1 rounded-full border bg-background px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground shadow-xs cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
          {blockTypeLabel(block.type)}
        </button>
        <div className="flex items-center gap-0.5 rounded-full border bg-background shadow-xs px-1 py-0.5">
          {block.type !== 'divider' && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(block)} title={t('people_landing.edit', 'Editar')}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <span className="mx-0.5 inline-flex rounded-full bg-muted/60 p-0.5" title={t('people_landing.editor.width', 'Ancho')}>
            {widths.map((w) => (
              <button
                key={w.v}
                type="button"
                aria-pressed={span === w.v}
                onClick={() => onSetSpan(block, w.v)}
                className={cn(
                  'h-6 min-w-6 rounded-full px-1.5 text-[11px] font-semibold transition-colors',
                  span === w.v ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {w.label}
              </button>
            ))}
          </span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDuplicate(block)} title={t('people_landing.editor.duplicate', 'Duplicar')}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => move(-1)} disabled={idx === 0} title={t('people_landing.editor.move_up', 'Subir')}>
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => move(1)} disabled={idx === total - 1} title={t('people_landing.editor.move_down', 'Bajar')}>
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => onDelete(block)} title={t('common.ui.delete', 'Eliminar')}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <BlockRenderer block={block} imageUrls={imageUrls} />
    </div>
  );
}
